import React, { useMemo, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  Typography
} from '@mui/material'
import { CheckCircle, Payment, Refresh } from '@mui/icons-material'
import { WARZONE_COIN_IMAGE_URL } from '../../../properties'
import { useWarzoneCoins } from '../../../contexts/WarzoneCoinsContext'
import { usePalworldCart } from '../../../contexts/PalworldCartContext'
import { usePalworldPerfil } from '../../../contexts/PalworldPerfilContext'
import { apiService } from '../../../services/api'

const formatCoins = (value: number) => value.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
const roundCoins = (value: number) => Math.round((Number(value) || 0) * 100) / 100
const VIP_DISCOUNT_RATE = 0.1

const Coin: React.FC<{ size?: number }> = ({ size = 16 }) => (
  <Box
    component="img"
    src={WARZONE_COIN_IMAGE_URL}
    alt="Warzone Coin"
    sx={{ width: size, height: size, opacity: 0.95 }}
    loading="lazy"
  />
)

const PalworldCartSummary: React.FC = () => {
  const { saldo, isLoading: isSaldoLoading, error: saldoError, refresh: refreshSaldo } = useWarzoneCoins()
  const { state, clearCart, toggleCart } = usePalworldCart()
  const { perfil, isLoading: isPerfilLoading } = usePalworldPerfil()

  const [confirmOpen, setConfirmOpen] = useState(false)
  const [successOpen, setSuccessOpen] = useState(false)
  const [isProcessing, setIsProcessing] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [confirmSaldo, setConfirmSaldo] = useState<number | null>(null)
  const [isConfirmSaldoLoading, setIsConfirmSaldoLoading] = useState(false)

  const saldoValue = saldo
  const subtotal = state.total
  const vipDiasRestantes = Number(perfil?.vipAtual?.diasRestantes ?? 0) || 0
  const isVip = vipDiasRestantes > 0
  const vipDiscount = isVip ? roundCoins(subtotal * VIP_DISCOUNT_RATE) : 0
  const totalToPay = isVip ? roundCoins(subtotal - vipDiscount) : subtotal

  const canAdvance = useMemo(() => {
    if (state.items.length === 0) return false
    if (confirmSaldo === null || confirmSaldo === undefined) return false
    return totalToPay <= confirmSaldo
  }, [confirmSaldo, state.items.length, totalToPay])

  const openConfirm = async () => {
    setError(null)
    setConfirmSaldo(null)
    setConfirmOpen(true)
    try {
      setIsConfirmSaldoLoading(true)
      const extrato = await apiService.getWarzoneCoinsExtrato({ page: 0, size: 1 })
      setConfirmSaldo(Number(extrato?.saldo ?? 0) || 0)
      await refreshSaldo()
    } catch {
      // ignore (context already stores error)
    } finally {
      setIsConfirmSaldoLoading(false)
    }
  }

  const doCheckout = async () => {
    setError(null)
    setIsProcessing(true)
    try {
      const latestSaldo = confirmSaldo ?? (Number((await apiService.getWarzoneCoinsExtrato({ page: 0, size: 1 }))?.saldo ?? 0) || 0)
      if (totalToPay > latestSaldo) {
        throw new Error(
          `Saldo insuficiente. Total${isVip ? ' (com desconto VIP)' : ''}: ${formatCoins(totalToPay)} • Saldo: ${formatCoins(latestSaldo)} warzone_coins`
        )
      }

      const payload = {
        items: state.items.map((l) => ({
          itemId: l.item.id,
          quantity: l.quantity
        }))
      }

      await apiService.comprarPalworldItems(payload)

      clearCart()
      setConfirmOpen(false)
      setSuccessOpen(true)
      toggleCart()
      await refreshSaldo()
    } catch (e: any) {
      setError(e?.message || 'Erro ao finalizar compra.')
    } finally {
      setIsProcessing(false)
    }
  }

  if (state.items.length === 0) return null

  return (
    <Box sx={{ p: 3, backgroundColor: 'rgba(0,230,118,0.05)', borderRadius: 2, border: '1px solid rgba(0,230,118,0.2)' }}>
      <Typography variant="h6" fontWeight={900} sx={{ mb: 2 }}>
        Resumo do carrinho
      </Typography>

      <Stack spacing={1.5}>
        {saldoError && <Alert severity="warning">{saldoError}</Alert>}

        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Typography variant="body2" color="text.secondary">
            Itens:
          </Typography>
          <Chip label={state.totalItems} size="small" sx={{ bgcolor: '#00e676', color: '#06110b', fontWeight: 900 }} />
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            VIP:
          </Typography>
          <Chip
            size="small"
            color={isVip ? 'success' : 'default'}
            variant="outlined"
            label={
              isPerfilLoading && !perfil
                ? 'Carregando…'
                : isVip
                  ? `Ativo • ${vipDiasRestantes.toLocaleString('pt-BR')}d`
                  : 'Não'
            }
            sx={{ borderColor: 'rgba(255,255,255,0.16)', fontWeight: 800 }}
          />
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            Subtotal:
          </Typography>
          <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
            <Typography variant="body2" fontWeight={900}>
              {formatCoins(subtotal)}
            </Typography>
            <Coin />
          </Stack>
        </Box>

        {isVip && (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              Desconto VIP (10%):
            </Typography>
            <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
              <Typography variant="body2" fontWeight={900} sx={{ color: '#00e676' }}>
                -{formatCoins(vipDiscount)}
              </Typography>
              <Coin />
            </Stack>
          </Box>
        )}

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            Total a pagar:
          </Typography>
          <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
            <Typography variant="body2" fontWeight={900}>
              {formatCoins(totalToPay)}
            </Typography>
            <Coin />
          </Stack>
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            Seu saldo:
          </Typography>
          <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
            {isSaldoLoading && saldoValue === null ? (
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                Carregando…
              </Typography>
            ) : (
              <>
                <Typography variant="body2" fontWeight={900}>
                  {formatCoins(Number(saldoValue ?? 0) || 0)}
                </Typography>
                <Coin />
              </>
            )}
            <Button size="small" variant="text" startIcon={<Refresh />} onClick={() => void refreshSaldo()} sx={{ minWidth: 0 }}>
              Atualizar
            </Button>
          </Stack>
        </Box>

        <Divider sx={{ opacity: 0.3 }} />

        {saldoValue != null && totalToPay > saldoValue && (
          <Alert severity="error">
            Saldo insuficiente para finalizar a compra.
          </Alert>
        )}

        <Stack spacing={1} sx={{ mt: 1 }}>
          <Button
            fullWidth
            variant="contained"
            size="large"
            startIcon={<Payment />}
            onClick={openConfirm}
            disabled={state.items.length === 0}
            sx={{ py: 1.4, fontWeight: 900, background: 'linear-gradient(45deg, #00e676 30%, #4caf50 90%)' }}
          >
            Finalizar compra
          </Button>

          <Button fullWidth variant="outlined" size="small" onClick={clearCart} sx={{ color: 'text.secondary' }}>
            Limpar carrinho
          </Button>
        </Stack>
      </Stack>

      <Dialog
        open={confirmOpen}
        onClose={() => (isProcessing ? null : setConfirmOpen(false))}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            backgroundColor: '#0c0d18',
            border: '1px solid rgba(0,230,118,0.18)'
          }
        }}
      >
        <DialogTitle sx={{ fontWeight: 900 }}>Finalizar compra</DialogTitle>
        <DialogContent dividers>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          <Stack spacing={1.25}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography sx={{ color: 'text.secondary' }}>VIP</Typography>
              <Chip
                size="small"
                color={isVip ? 'success' : 'default'}
                variant="outlined"
                label={
                  isPerfilLoading && !perfil
                    ? 'Carregando…'
                    : isVip
                      ? `Ativo • ${vipDiasRestantes.toLocaleString('pt-BR')}d`
                      : 'Não'
                }
                sx={{ borderColor: 'rgba(255,255,255,0.16)', fontWeight: 800 }}
              />
            </Box>

            <Typography>
              Subtotal:
            </Typography>
            <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
              <Typography sx={{ fontWeight: 900, fontSize: 20 }}>{formatCoins(subtotal)}</Typography>
              <Coin size={18} />
            </Stack>

            {isVip && (
              <>
                <Typography sx={{ mt: 1 }}>
                  Desconto VIP (10%):
                </Typography>
                <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                  <Typography sx={{ fontWeight: 900, fontSize: 20, color: '#00e676' }}>-{formatCoins(vipDiscount)}</Typography>
                  <Coin size={18} />
                </Stack>
              </>
            )}

            <Typography sx={{ mt: 1 }}>
              Total a pagar:
            </Typography>
            <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
              <Typography sx={{ fontWeight: 900, fontSize: 20 }}>{formatCoins(totalToPay)}</Typography>
              <Coin size={18} />
            </Stack>

            <Typography sx={{ mt: 1 }}>
              Você tem:
            </Typography>
            <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
              {isConfirmSaldoLoading ? (
                <Typography sx={{ color: 'text.secondary' }}>Carregando…</Typography>
              ) : (
                <>
                  <Typography sx={{ fontWeight: 900, fontSize: 20 }}>
                    {formatCoins(Number(confirmSaldo ?? saldoValue ?? 0) || 0)}
                  </Typography>
                  <Coin size={18} />
                  <Typography sx={{ color: 'text.secondary' }}>warzone_coins</Typography>
                </>
              )}
            </Stack>

            {confirmSaldo != null && totalToPay > confirmSaldo && (
              <Alert severity="error">Saldo insuficiente. Não é possível avançar.</Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button variant="outlined" onClick={() => setConfirmOpen(false)} disabled={isProcessing}>
            Cancelar
          </Button>
          <Button variant="contained" onClick={doCheckout} disabled={!canAdvance || isProcessing}>
            {isProcessing ? 'Processando…' : 'Confirmar'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={successOpen}
        onClose={() => setSuccessOpen(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            backgroundColor: '#0c0d18',
            border: '1px solid rgba(0,230,118,0.18)'
          }
        }}
      >
        <DialogTitle sx={{ fontWeight: 900, display: 'flex', alignItems: 'center', gap: 1 }}>
          <CheckCircle color="success" />
          Compra finalizada
        </DialogTitle>
        <DialogContent dividers>
          <Typography sx={{ color: 'text.secondary' }}>
            Sua compra foi enviada para processamento. Você pode acompanhar em “Meus Pedidos”.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button variant="contained" onClick={() => setSuccessOpen(false)}>
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default PalworldCartSummary
